"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.initRUM = void 0;
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _browserRum = require("@datadog/browser-rum");
var _browserLogs = require("@datadog/browser-logs");
var _utils = require("../utils");
var _environment = require("@studio/environment");
var _heartbeat = require("./heartbeat");
var _sendToDatadog = require("./sendToDatadog");
var initialized = false;

/**
 * Initialize Datadog RUM and Logs
 * This function is called on initial application load
 * The user ID is set in the companion applications App.js as soon as the user data is available
 * @param appName
 * @param version
 * @param forwardConsoleLogs
 * @param forwardErrorsToLogs
 * @param replaySampleRate
 * @param enableHeartbeat
 */
var initRUM = exports.initRUM = function initRUM(appName, version) {
  var forwardConsoleLogs = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ['error', 'warn', 'log'];
  var forwardErrorsToLogs = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
  var replaySampleRate = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 30;
  var enableHeartbeat = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : false;
  var datadogEnv = (0, _environment.getEnvVar)('REACT_APP_CVENT_ENV') || '';
  if (initialized || datadogEnv === 'local') {
    if (enableHeartbeat) {
      (0, _heartbeat.startHeartbeat)();
    }
    return;
  }
  var datadogApplicationId = (0, _environment.getEnvVar)('REACT_APP_DD_RUM_APP_ID') || '';
  var datadogClientToken = (0, _environment.getEnvVar)('REACT_APP_DD_RUM_CLIENT_TOKEN') || '';
  var isEnvironmentVarsMissing = !datadogEnv || !datadogApplicationId || !datadogClientToken;
  if (isEnvironmentVarsMissing) {
    console.error('Datadog RUM and Logs initialization failed due to missing environment variables');
    return;
  }
  var origin = window.location.origin;
  _browserRum.datadogRum.init({
    applicationId: datadogApplicationId,
    clientToken: datadogClientToken,
    service: appName,
    env: datadogEnv,
    version: version,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    sessionSampleRate: 100,
    sessionReplaySampleRate: replaySampleRate,
    trackSessionAcrossSubdomains: true,
    useSecureSessionCookie: true,
    traceSampleRate: 20,
    traceContextInjection: 'all',
    allowedTracingUrls: [origin, "".concat(origin, "/api")],
    // Dynamically get API URL since sg50, ld50 and pr50 have different URLs
    useCrossSiteSessionCookie: true
  });
  _browserLogs.datadogLogs.init({
    env: datadogEnv,
    service: appName,
    version: version,
    sessionSampleRate: 100,
    clientToken: datadogClientToken,
    forwardErrorsToLogs: forwardErrorsToLogs,
    forwardConsoleLogs: forwardConsoleLogs,
    trackSessionAcrossSubdomains: true,
    useSecureSessionCookie: true,
    useCrossSiteSessionCookie: true
  });

  // Validates the initialization status of Datadog RUM and Logs SDKs.
  try {
    var _rumContext$view, _rumContext$user;
    var rumContext = _browserRum.datadogRum.getInternalContext();
    var logsContext = _browserLogs.datadogLogs.getInternalContext();

    // Validate RUM context has required properties
    // https://docs.datadoghq.com/real_user_monitoring/browser/troubleshooting/#:~:text=the%20intake%20endpoint.-,The%20RUM%20Browser%20SDK%20is%20initialized,-Check%20if%20the
    var isRumInitialized = !!(rumContext !== null && rumContext !== void 0 && rumContext.application_id && rumContext !== null && rumContext !== void 0 && rumContext.session_id && rumContext !== null && rumContext !== void 0 && rumContext.view);

    // Validate Logs context has required properties
    // https://docs.datadoghq.com/logs/log_collection/javascript/#:~:text=granted%27)%3B%0A%7D)%3B-,Access%20internal%20context,-After%20the%20Datadog
    var isLogsInitialized = !!(logsContext !== null && logsContext !== void 0 && logsContext.session_id);
    var initializationStatus = {
      isRumInitialized: isRumInitialized,
      isLogsInitialized: isLogsInitialized,
      rumContext: {
        application_id: rumContext === null || rumContext === void 0 ? void 0 : rumContext.application_id,
        session_id: rumContext === null || rumContext === void 0 ? void 0 : rumContext.session_id,
        view_id: rumContext === null || rumContext === void 0 || (_rumContext$view = rumContext.view) === null || _rumContext$view === void 0 ? void 0 : _rumContext$view.id,
        user_id: rumContext === null || rumContext === void 0 || (_rumContext$user = rumContext.user) === null || _rumContext$user === void 0 ? void 0 : _rumContext$user.id
      },
      logsContext: {
        session_id: logsContext === null || logsContext === void 0 ? void 0 : logsContext.session_id
      }
    };
    if (!isRumInitialized || !isLogsInitialized) {
      (0, _sendToDatadog.sendToDatadog)('datadog_initialization_status', initializationStatus, 'datadog failed to initialize - missing required context properties', true);
    } else {
      initialized = true;
      (0, _sendToDatadog.sendToDatadog)('datadog_initialization_status', initializationStatus, 'datadog initialized successfully');
      if (enableHeartbeat) {
        (0, _heartbeat.startHeartbeat)();
      }
    }
  } catch (error) {
    (0, _sendToDatadog.sendToDatadog)('datadog_initialization_status', {
      error: error.message
    }, 'datadog failed to initialize', true);
  }
  var _getURLPathParams = (0, _utils.getURLPathParams)(),
    teamId = _getURLPathParams.teamId,
    eventId = _getURLPathParams.eventId,
    broadcastId = _getURLPathParams.broadcastId,
    showId = _getURLPathParams.showId;

  /**
   * Forwarded console.error logs, uncaught exceptions and network errors,
   * will not have these tags set by default,
   * adding them globally to all logs and RUM sessions
   * Some of these are duplicated until we can address the varied nature of our logging in WEBINAR-24489
   */
  Object.entries({
    'studio.team.id': teamId,
    'studio.event.id': eventId,
    'studio.broadcast.id': broadcastId,
    'studio.show.id': showId,
    teamId: teamId,
    eventId: eventId,
    broadcastId: broadcastId
  }).forEach(function (_ref) {
    var _ref2 = (0, _slicedToArray2["default"])(_ref, 2),
      key = _ref2[0],
      value = _ref2[1];
    if (value) {
      _browserRum.datadogRum.setGlobalContextProperty(key, value);
      _browserLogs.datadogLogs.setGlobalContextProperty(key, value);
    }
  });
};